import { useEffect, useState } from "react";
import { Loader2, Download, Sparkles, Image, Video } from "lucide-react";

interface SimpleVideoPlayerProps {
  jobId: string;
  inputPrompt?: string;
  productImageUrl?: string;
  referenceVideoUrl?: string;
}

const SimpleVideoPlayer = ({
  jobId,
  inputPrompt,
  productImageUrl,
  referenceVideoUrl,
}: SimpleVideoPlayerProps) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>("pending");
  const [progress, setProgress] = useState<string>("");

  useEffect(() => {
    let pollInterval: NodeJS.Timeout;
    let isActive = true;

    const fetchVideo = async () => {
      if (!isActive) return;

      try {
        const response = await fetch(
          `https://ai-marketplace-backend.passionbits.io/api/video-generation/download/${jobId}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get("content-type");
        console.log("Response Content-Type:", contentType);

        if (contentType?.includes("application/json")) {
          const data = await response.json();
          console.log("Status data:", data);

          if (!isActive) return;

          setStatus(data.status);
          setProgress(data.progress || "");

          if (data.status === "completed") {
            setTimeout(fetchVideo, 1000);
          } else if (data.status === "failed") {
            setError(data.error || "Video generation failed");
            setLoading(false);
            if (pollInterval) clearInterval(pollInterval);
          }
        } else if (
          contentType?.includes("video") ||
          contentType?.includes("octet-stream")
        ) {
          console.log("Received binary video data");

          const blob = await response.blob();
          console.log("Blob size:", blob.size, "Blob type:", blob.type);

          if (!isActive) return;

          const videoBlob = new Blob([blob], { type: "video/mp4" });
          const blobUrl = URL.createObjectURL(videoBlob);

          setVideoUrl(blobUrl);
          setLoading(false);
          if (pollInterval) clearInterval(pollInterval);
        } else {
          console.log("Unknown content type, treating as binary");
          const blob = await response.blob();

          if (!isActive) return;

          const videoBlob = new Blob([blob], { type: "video/mp4" });
          const blobUrl = URL.createObjectURL(videoBlob);

          setVideoUrl(blobUrl);
          setLoading(false);
          if (pollInterval) clearInterval(pollInterval);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        if (!isActive) return;
        
        setError(err instanceof Error ? err.message : "Failed to fetch video");
        setLoading(false);
        if (pollInterval) clearInterval(pollInterval);
      }
    };

    fetchVideo();
    pollInterval = setInterval(fetchVideo, 5000);

    return () => {
      isActive = false;
      if (pollInterval) clearInterval(pollInterval);
      if (videoUrl && videoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [jobId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 relative">
            <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Generating Your Video
          </h3>
          <p className="text-gray-600 mb-4">
            {progress || "Processing your request..."}
          </p>
          <div className="bg-gray-100 rounded-full h-1.5 overflow-hidden mb-4">
            <div
              className="bg-blue-600 h-full transition-all duration-500"
              style={{ width: status === "processing" ? "60%" : "30%" }}
            ></div>
          </div>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <span className="inline-block w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
            <span>{status}</span>
          </div>
          <p className="text-xs text-gray-400 mt-4 font-mono">ID: {jobId}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg border-2 border-red-200 p-8 max-w-md text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-7 h-7 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Generation Failed
          </h3>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <p className="text-xs text-gray-400 font-mono">Job ID: {jobId}</p>
        </div>
      </div>
    );
  }

  if (!videoUrl) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
          <p className="text-gray-600">Preparing video...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Success Badge */}
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-green-200 text-green-700 px-4 py-2 rounded-full shadow-sm text-sm font-medium">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            Video Generated Successfully
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Video Section */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Generated Video
                </h2>
                <p className="text-xs text-gray-500 mt-0.5 font-mono">
                  ID: {jobId}
                </p>
              </div>

              <div className="bg-black aspect-video">
                <video
                  key={videoUrl}
                  src={videoUrl}
                  controls
                  autoPlay
                  loop
                  playsInline
                  preload="auto"
                  className="w-full h-full"
                  onLoadedMetadata={() => console.log("Video metadata loaded")}
                  onCanPlay={() => console.log("Video can play")}
                  onError={(e) => {
                    console.error("Video playback error:", e);
                    const videoElement = e.target as HTMLVideoElement;
                    console.error(
                      "Video error code:",
                      videoElement.error?.code
                    );
                    console.error(
                      "Video error message:",
                      videoElement.error?.message
                    );
                    setError(
                      "Failed to play video. The file may be corrupted."
                    );
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <button
                  onClick={() => {
                    const a = document.createElement("a");
                    a.href = videoUrl;
                    a.download = `video-${jobId}.mp4`;
                    a.click();
                  }}
                  className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-sm"
                >
                  <Download className="w-4 h-4" />
                  Download Video
                </button>
              </div>
            </div>
          </div>

          {/* Input Details Sidebar */}
          <div className="space-y-4">
            {/* Prompt Card */}
            {inputPrompt && (
              <div className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                  <Sparkles className="w-4 h-4 text-gray-400" />
                  Input Prompt
                </h3>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {inputPrompt}
                  </p>
                </div>
              </div>
            )}

            {/* Product Image Card */}
            {productImageUrl && (
              <div className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                  <Image className="w-4 h-4 text-gray-400" />
                  Product Image
                </h3>
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={productImageUrl}
                    alt="Product"
                    className="w-full h-44 object-cover"
                  />
                </div>
              </div>
            )}

            {/* Reference Video Card */}
            {referenceVideoUrl && (
              <div className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                  <Video className="w-4 h-4 text-gray-400" />
                  Reference Video
                </h3>
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <video
                    src={referenceVideoUrl}
                    controls
                    className="w-full h-44 object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleVideoPlayer;